import { useState } from "react";
import { useTranslation } from "react-i18next";
import { HoverPopover } from "@/components/ui/HoverPopover";
import { TruncatedText } from "@/components/ui/TruncatedText";
import { BookingLineItemChips } from "./BookingLineItemChips";
import type { BookingListRow } from "./bookingViewModel";
import { bookingPartyCount } from "./bookingPartyCount";
import { cn } from "@/lib/utils";

const MAX_VISIBLE_ITEMS = 2;

interface BookingPartyItemsCellProps {
  booking: BookingListRow;
  className?: string;
  /** Inline text for compact horizontal rows; list for mobile card body; chips for wider layouts. */
  variant?: "chips" | "inline" | "list";
}

function formatInlinePartySummary(
  booking: BookingListRow,
  guestLabel: string,
): string {
  if (booking.lineItems.length > 0) {
    return booking.lineItems
      .map((line) => `${line.quantity}× ${line.name}`)
      .join(", ");
  }

  if (bookingPartyCount(booking) > 0) {
    return guestLabel;
  }

  return "";
}

export function BookingPartyItemsCell({
  booking,
  className,
  variant = "chips",
}: BookingPartyItemsCellProps) {
  const { t } = useTranslation("bookings");
  const [expanded, setExpanded] = useState(false);
  const partyCount = bookingPartyCount(booking);
  const guestLabel = t("guest", { count: partyCount });

  if (variant === "list") {
    if (booking.lineItems.length > 0) {
      return (
        <ul
          className={cn(
            "mt-2 list-none space-y-0.5 text-sm text-surface-600",
            className,
          )}
        >
          {booking.lineItems.map((line) => (
            <li key={line.id}>
              {line.quantity}× {line.name}
            </li>
          ))}
        </ul>
      );
    }

    if (partyCount > 0) {
      return (
        <ul
          className={cn(
            "mt-2 list-none space-y-0.5 text-sm text-surface-600",
            className,
          )}
        >
          <li>{guestLabel}</li>
        </ul>
      );
    }

    return null;
  }

  if (variant === "inline") {
    const summary = formatInlinePartySummary(booking, guestLabel);
    if (!summary) {
      return null;
    }
    return (
      <TruncatedText
        as="p"
        className={cn("text-sm leading-snug text-surface-600", className)}
      >
        {summary}
      </TruncatedText>
    );
  }

  if (booking.lineItems.length === 0) {
    if (partyCount > 0) {
      return (
        <span className={cn("text-sm text-surface-700", className)}>
          {guestLabel}
        </span>
      );
    }
    return <span className={cn("text-sm text-surface-400", className)}>—</span>;
  }

  const visibleItems = booking.lineItems.slice(0, MAX_VISIBLE_ITEMS);
  const hiddenCount = booking.lineItems.length - visibleItems.length;
  const fullList = (
    <BookingLineItemChips items={booking.lineItems} className="max-w-xs" />
  );

  if (hiddenCount <= 0) {
    return (
      <div className={cn("min-w-0", className)}>
        <BookingLineItemChips items={visibleItems} />
      </div>
    );
  }

  const moreLabel = t("partyItems.andMore", { count: hiddenCount });

  return (
    <div className={cn("min-w-0 space-y-1", className)}>
      <BookingLineItemChips items={visibleItems} />
      <div className="sm:hidden">
        <button
          type="button"
          className="text-xs font-medium text-primary-600 hover:text-primary-700 hover:underline"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setExpanded((prev) => !prev);
          }}
        >
          {moreLabel}
        </button>
        {expanded ? (
          <div className="mt-1.5" onClick={(e) => e.stopPropagation()}>
            {fullList}
          </div>
        ) : null}
      </div>
      <div className="hidden sm:block">
        <HoverPopover content={fullList} placement="below">
          <button
            type="button"
            className="text-xs font-medium text-primary-600 hover:text-primary-700 hover:underline"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            {moreLabel}
          </button>
        </HoverPopover>
      </div>
    </div>
  );
}
